import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Calendar, Plane, ArrowRight, CheckCircle2, XCircle, MapPin } from 'lucide-react';
import { fmtDateTime, fmtDate, fmtRelative } from '@/lib/dates';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

export default function ApprovalInboxPage() {
  const [data, setData] = useState({ agendas: [], sppds: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: '', kind: '', item: null });
  const [reason, setReason] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/dashboard/pending-approvals').then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const doAction = async () => {
    const path = modal.kind === 'agenda' ? `/agenda/${modal.item.id}/${modal.type}` : `/sppd/${modal.item.id}/${modal.type}`;
    try {
      await api.post(path, { catatan: reason });
      toast.success(modal.type === 'approve' ? 'Berhasil disetujui' : 'Berhasil ditolak');
      setModal({ open: false, type: '', kind: '', item: null });
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    }
  };

  const openModal = (kind, type, item) => {
    setModal({ open: true, kind, type, item });
    setReason('');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inbox Persetujuan"
        subtitle="Tinjau dan persetujui agenda serta SPPD yang menunggu validasi Anda."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Approval' }]}
      />

      <Tabs defaultValue="agenda">
        <TabsList>
          <TabsTrigger value="agenda" data-testid="approval-tab-agenda">
            Agenda <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">{data.agendas.length}</span>
          </TabsTrigger>
          <TabsTrigger value="sppd" data-testid="approval-tab-sppd">
            SPPD <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">{data.sppds.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agenda" className="mt-4">
          {loading ? null : data.agendas.length === 0 ? (
            <EmptyState title="Tidak ada agenda menunggu persetujuan" description="Anda sudah menyelesaikan semua approval agenda." />
          ) : (
            <div className="grid gap-3">
              {data.agendas.map((a) => (
                <Card key={a.id} className="border-slate-200 shadow-premium">
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="w-14 h-14 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-bold text-slate-900 text-base">{a.nama_acara}</h3>
                          <StatusBadge status={a.tingkat_urgensi} type="urgensi" />
                        </div>
                        <div className="text-sm text-slate-600"><MapPin className="w-3.5 h-3.5 inline -mt-0.5" /> {a.lokasi}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          <Calendar className="w-3 h-3 inline" /> {fmtDateTime(a.tanggal_mulai)} · Diajukan {fmtRelative(a.created_at)} oleh {a.created_by_nama || 'Operator'}
                        </div>
                        {a.delegasi_nama && (
                          <div className="mt-2 text-xs px-2 py-1 inline-block rounded bg-amber-50 text-amber-800">Didelegasikan kepada {a.delegasi_nama}</div>
                        )}
                      </div>
                      <div className="flex md:flex-col items-center gap-2 flex-shrink-0">
                        <Button onClick={() => openModal('agenda', 'approve', a)} data-testid={`approve-agenda-${a.id}`} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-2" />Setujui</Button>
                        <Button onClick={() => openModal('agenda', 'reject', a)} data-testid={`reject-agenda-${a.id}`} variant="destructive"><XCircle className="w-4 h-4 mr-2" />Tolak</Button>
                        <Link to={`/agenda/${a.id}`}><Button variant="outline"><ArrowRight className="w-4 h-4" /></Button></Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sppd" className="mt-4">
          {loading ? null : data.sppds.length === 0 ? (
            <EmptyState title="Tidak ada SPPD menunggu persetujuan" description="Anda sudah menyelesaikan semua approval SPPD." />
          ) : (
            <div className="grid gap-3">
              {data.sppds.map((s) => (
                <Card key={s.id} className="border-slate-200 shadow-premium">
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="w-14 h-14 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
                        <Plane className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{s.nomor_sppd}</span>
                          <h3 className="font-bold text-slate-900">{s.pejabat_nama}</h3>
                        </div>
                        <div className="text-sm text-slate-600">{s.keperluan}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          <MapPin className="w-3 h-3 inline" /> {s.tujuan_kota}, {s.tujuan_provinsi} · {fmtDate(s.tanggal_berangkat)} - {fmtDate(s.tanggal_kembali)}
                        </div>
                      </div>
                      <div className="flex md:flex-col items-center gap-2 flex-shrink-0">
                        <Button onClick={() => openModal('sppd', 'approve', s)} data-testid={`approve-sppd-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-2" />Setujui</Button>
                        <Button onClick={() => openModal('sppd', 'reject', s)} data-testid={`reject-sppd-${s.id}`} variant="destructive"><XCircle className="w-4 h-4 mr-2" />Tolak</Button>
                        <Link to={`/sppd/${s.id}`}><Button variant="outline"><ArrowRight className="w-4 h-4" /></Button></Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{modal.type === 'approve' ? `Setujui ${modal.kind === 'agenda' ? 'Agenda' : 'SPPD'}` : `Tolak ${modal.kind === 'agenda' ? 'Agenda' : 'SPPD'}`}</DialogTitle>
          </DialogHeader>
          <Textarea placeholder={modal.type === 'approve' ? 'Catatan (opsional)' : 'Alasan penolakan...'} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false, type: '', kind: '', item: null })}>Batal</Button>
            {modal.type === 'approve' ? (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doAction}>Setujui</Button>
            ) : (
              <Button variant="destructive" onClick={doAction}>Tolak</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
